import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  accent: '#4FC3F7',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3'
};

// Options pour afficher les labels
const CATEGORIE_LABELS = {
  securite: { label: 'Sécurité', icon: 'bi-shield-check', color: '#d32f2f' },
  formation: { label: 'Formation', icon: 'bi-mortarboard', color: '#1976d2' },
  procedure: { label: 'Procédure', icon: 'bi-clipboard-check', color: '#388e3c' },
  technique: { label: 'Technique', icon: 'bi-tools', color: '#f57c00' },
  autre: { label: 'Autre', icon: 'bi-three-dots', color: '#757575' }
};

// Fonction pour obtenir le label du statut
const getStatusLabel = (statut) => {
  switch (statut) {
    case "publie":
      return "Publié";
    case "brouillon":
      return "Brouillon";
    case "archive":
      return "Archivé";
    default:
      return statut;
  }
};

// Options de changement de statut
const STATUS_OPTIONS = {
  publie: [
    { value: "brouillon", label: "Mettre en brouillon", icon: "bi-file-earmark", color: COLORS.warning },
    { value: "archive", label: "Archiver", icon: "bi-archive", color: "#757575" }
  ],
  brouillon: [
    { value: "publie", label: "Publier", icon: "bi-eye", color: COLORS.success },
    { value: "archive", label: "Archiver", icon: "bi-archive", color: "#757575" }
  ],
  archive: [
    { value: "publie", label: "Publier", icon: "bi-eye", color: COLORS.success },
    { value: "brouillon", label: "Mettre en brouillon", icon: "bi-file-earmark", color: COLORS.warning }
  ]
};

// Composant VideoPlayer pour les détails
const VideoPlayer = ({ videoUrl, duration, categoryColor }) => {
  const videoRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Mettre à jour le temps courant
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateTime = () => {
      setCurrentTime(video.currentTime);
    };

    video.addEventListener('timeupdate', updateTime);
    return () => video.removeEventListener('timeupdate', updateTime);
  }, []);

  // Formater la durée
  const formatDuration = (seconds) => {
    if (!seconds) return "00:00";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCurrentTime = () => {
    return formatDuration(currentTime);
  };

  // Calculer le pourcentage de progression
  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Gérer les erreurs
  const handleError = () => {
    console.error(`Erreur de chargement de la vidéo: ${videoUrl}`);
    setHasError(true);
  };

  // Basculer play/pause
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play().then(() => {
        setIsPlaying(true);
      }).catch(error => {
        console.error("Erreur de lecture:", error);
        setHasError(true);
      });
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  // Changer le volume
  const toggleMute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = !video.muted;
      setIsMuted(video.muted);
    }
  };

  // Changer la position de la vidéo
  const handleProgressClick = (e) => {
    const video = videoRef.current;
    if (!video || duration === 0) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const percent = clickX / width;
    const newTime = percent * duration;

    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  if (hasError) {
    return (
      <div className="video-error-container" style={{
        backgroundColor: '#1a1a1a',
        aspectRatio: '16/9',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        borderRadius: '8px'
      }}>
        <i className="bi bi-exclamation-triangle fs-1 mb-3" style={{ color: '#ff4444' }}></i>
        <span className="mb-2">Erreur de chargement de la vidéo</span>
      </div>
    );
  }

  return (
    <div 
      className="video-player-container"
      style={{
        position: 'relative',
        backgroundColor: '#000',
        aspectRatio: '16/9',
        overflow: 'hidden',
        cursor: 'pointer',
        borderRadius: '8px'
      }}
      onMouseEnter={() => {
        setIsHovering(true);
        setShowControls(true);
      }}
      onMouseLeave={() => {
        setIsHovering(false);
        if (!isPlaying) {
          setShowControls(false);
        }
      }}
      onClick={(e) => {
        if (!e.target.closest('.video-controls')) {
          togglePlay();
        }
      }}
    >
      {/* Élément vidéo */}
      <video
        ref={videoRef}
        src={videoUrl}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          backgroundColor: '#000'
        }}
        preload="metadata"
        onError={handleError}
        playsInline
      />

      {/* Overlay de play/pause */}
      {!isPlaying && (
        <div 
          className="video-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            transition: 'background-color 0.3s ease'
          }}
        >
          <div 
            className="play-button"
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: categoryColor,
              fontSize: '2.5rem',
              transition: 'all 0.3s ease',
              transform: isHovering ? 'scale(1.1)' : 'scale(1)'
            }}
          >
            <i className="bi bi-play-fill"></i>
          </div>
        </div>
      )}

      {/* Contrôles vidéo */}
      {(isPlaying || showControls || isHovering) && (
        <div 
          className="video-controls"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            padding: '15px',
            background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))',
            color: '#fff',
            transition: 'opacity 0.3s ease',
            opacity: isHovering || isPlaying ? 1 : 0
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Barre de progression */}
          <div 
            className="progress-bar-container"
            style={{
              width: '100%',
              height: '5px',
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '3px',
              marginBottom: '12px',
              cursor: 'pointer'
            }}
            onClick={handleProgressClick}
          >
            <div 
              className="progress-bar"
              style={{
                width: `${progressPercentage}%`,
                height: '100%',
                backgroundColor: categoryColor,
                borderRadius: '3px',
                transition: 'width 0.1s linear'
              }}
            />
          </div>

          {/* Contrôles inférieurs */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* Bouton play/pause */}
              <button
                onClick={togglePlay}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontSize: '1.3rem',
                  cursor: 'pointer',
                  padding: '6px',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              >
                <i className={`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
              </button>

              {/* Bouton mute/unmute */}
              <button
                onClick={toggleMute}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontSize: '1.3rem',
                  cursor: 'pointer',
                  padding: '6px',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              >
                <i className={`bi ${isMuted ? 'bi-volume-mute-fill' : 'bi-volume-up-fill'}`}></i>
              </button>

              {/* Temps */}
              <span style={{ fontSize: '1rem', fontFamily: 'monospace' }}>
                {formatCurrentTime()} / {formatDuration(duration)}
              </span>
            </div>

            {/* Indicateur de lecture */}
            {isPlaying && (
              <span style={{
                fontSize: '0.9rem',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                padding: '4px 10px',
                borderRadius: '4px'
              }}>
                <i className="bi bi-play-circle me-1"></i>
                En cours
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Fonction pour obtenir le label du statut
const getStatusInfo = (statut, actif) => {
  const statusData = {
    publie: {
      text: actif ? 'Publié et actif' : 'Publié (inactif)',
      icon: 'bi-eye',
      color: actif ? COLORS.success : '#757575',
      bgColor: actif ? '#E8F5E9' : '#f5f5f5'
    },
    brouillon: {
      text: 'Brouillon',
      icon: 'bi-file-earmark',
      color: COLORS.warning,
      bgColor: '#FFF3E0'
    },
    archive: {
      text: 'Archivé',
      icon: 'bi-archive',
      color: '#757575',
      bgColor: '#f5f5f5'
    }
  };

  return statusData[statut] || {
    text: 'Inconnu',
    icon: 'bi-question-circle',
    color: COLORS.darkText,
    bgColor: COLORS.lightBg
  };
};

const TutorialDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [tuto, setTuto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentProfile, setCurrentProfile] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");

  useEffect(() => {
    // Récupérer le profil utilisateur
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setCurrentProfile(String(userData.profile || "").trim());
      } catch (error) {
        console.error("Error parsing user:", error);
      }
    }
    
    fetchTutoDetails();
    
    // Initialiser les tooltips Bootstrap
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    const tooltipList = tooltipTriggerList.map(function (tooltipTriggerEl) {
      return new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
    
    return () => {
      // Nettoyer les tooltips
      tooltipList.forEach(tooltip => tooltip.dispose());
    };
  }, [id]);

  const fetchTutoDetails = async () => {
    setLoading(true);
    setError("");
    
    try {
      const token = localStorage.getItem("access_token");
      const url = `${API_BASE_URL}/tuto/get.php?id=${id}`;
      
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await axios.get(url, {
        headers: headers,
        "Content-Type": "application/json",
      });
      
      if (response.data?.success) {
        setTuto(response.data.data);
      } else {
        setError(response.data?.message || "Erreur lors du chargement.");
      }
    } catch (err) {
      handleFetchError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchError = (err) => {
    console.error("Fetch error:", err);
    if (err.response?.status === 401) {
      setError("Session expirée. Veuillez vous reconnecter.");
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      setTimeout(() => navigate("/login"), 2000);
    } else if (err.response?.status === 404) {
      setError("Tutoriel non trouvé ou non disponible.");
    } else if (err.response?.status === 500) {
      setError("Erreur serveur. Veuillez contacter l'administrateur.");
    } else if (err.request) {
      setError("Serveur inaccessible. Vérifiez votre connexion.");
    } else {
      setError("Erreur technique lors du chargement des données.");
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "00:00";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    }
    return `${minutes}min ${secs}s`;
  };

  const getCategoryInfo = (categorie) => {
    return CATEGORIE_LABELS[categorie] || { 
      label: categorie, 
      icon: 'bi-tag', 
      color: COLORS.primary 
    };
  };

  const handleEdit = () => {
    navigate(`/tutos/edit/${id}`);
  };

  const handleGoBack = () => {
    if (currentProfile === "1") {
      navigate("/tutos/admin");
    } else {
      navigate("/tutos");
    }
  };

  // Fonction pour changer le statut
  const handleChangeStatus = async (newStatus) => {
    if (!tuto) return;
    
    if (!window.confirm(`Voulez-vous vraiment changer le statut de "${tuto.titre_video}" en "${getStatusLabel(newStatus)}"?`)) {
      return;
    }

    setIsChangingStatus(true);
    setActionError("");
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setActionError("Session expirée. Veuillez vous reconnecter.");
        return;
      }

      const url = `${API_BASE_URL}/tuto/change_statut.php?id=${tuto.id_video}`;
      
      const response = await axios.post(url, {
        statut_video: newStatus
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      if (response.data?.success) {
        // Mettre à jour le tutoriel localement
        setTuto(prev => ({
          ...prev,
          statut_video: newStatus,
          actif_video: newStatus === 'publie' ? 1 : 0,
          date_publication: response.data.data.tuto.date_publication
        }));
        
        setActionMessage(`Statut changé avec succès : ${response.data.message}`);
        setTimeout(() => setActionMessage(""), 3000);
      } else {
        setActionError(response.data?.message || "Erreur lors du changement de statut.");
      }
    } catch (err) {
      console.error("Status change error:", err);
      setActionError("Erreur lors du changement de statut.");
    } finally {
      setIsChangingStatus(false);
    }
  };

  // Fonction pour supprimer le tutoriel
  const handleDeleteTuto = () => {
    if (!tuto) return;
    setShowDeleteModal(true);
  };

  // Fonction pour confirmer la suppression
  const confirmDeleteTuto = async () => {
    if (!tuto) return;
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setActionError("Session expirée. Veuillez vous reconnecter.");
        return;
      }

      const url = `${API_BASE_URL}/tuto/delete.php?id=${tuto.id_video}`;
      
      const response = await axios.delete(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      if (response.data?.success) {
        setActionMessage(`Tutoriel supprimé avec succès. Redirection...`);
        setTimeout(() => navigate("/tutos/admin"), 1500);
      } else {
        setActionError(response.data?.message || "Erreur lors de la suppression.");
        setShowDeleteModal(false);
      }
    } catch (err) {
      console.error("Delete error:", err);
      setActionError("Erreur lors de la suppression.");
      setShowDeleteModal(false);
    }
  };

  const canEdit = currentProfile === "1";

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails du tutoriel</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to={currentProfile === "1" ? "/tutos/admin" : "/tutos"} style={{ color: COLORS.primary }}>
                  Tutoriels
                </Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Détails</li>
            </ol>
          </nav>
        </div>

        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body">
              <div className="text-center py-5">
                <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-3" style={{ color: COLORS.darkText }}>Chargement des détails du tutoriel...</p>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails du tutoriel</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to={currentProfile === "1" ? "/tutos/admin" : "/tutos"} style={{ color: COLORS.primary }}>
                  Tutoriels
                </Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Détails</li>
            </ol>
          </nav>
        </div>

        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body">
              <div className="text-center py-5">
                <div className="mb-4">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#FFEBEE',
                    color: COLORS.danger
                  }}>
                    <i className="bi bi-exclamation-triangle fs-1"></i>
                  </div>
                </div>
                <h5 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>Erreur</h5>
                <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                  {error}
                </p>
                
                <button
                  className="btn fw-semibold mt-4"
                  onClick={handleGoBack}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = COLORS.white;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = COLORS.primary;
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour aux tutoriels
                </button>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (!tuto) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails du tutoriel</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to={currentProfile === "1" ? "/tutos/admin" : "/tutos"} style={{ color: COLORS.primary }}>
                  Tutoriels
                </Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Détails</li>
            </ol>
          </nav>
        </div>

        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body">
              <div className="text-center py-5">
                <div className="mb-4">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#F5F7FA',
                    color: '#B0BEC5'
                  }}>
                    <i className="bi bi-question-circle fs-1"></i>
                  </div>
                </div>
                <h5 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>Tutoriel non trouvé</h5>
                <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                  Le tutoriel demandé n'existe pas ou n'est plus disponible.
                </p>
                
                <button
                  className="btn fw-semibold mt-4"
                  onClick={handleGoBack}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = COLORS.white;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = COLORS.primary;
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour aux tutoriels
                </button>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  const categoryInfo = getCategoryInfo(tuto.categorie_video);
  const statusInfo = getStatusInfo(tuto.statut_video, tuto.actif_video);

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails du tutoriel</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to={currentProfile === "1" ? "/tutos/admin" : "/tutos"} style={{ color: COLORS.primary }}>
                Tutoriels
              </Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>
              <span className="text-truncate" style={{ maxWidth: '300px', display: 'inline-block' }}>
                {tuto.titre_video}
              </span>
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-8">
            {/* Carte principale */}
            <div className="card mb-4" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                {/* En-tête avec titre, badges et actions - MODIFIÉ POUR CORRIGER L'ALIGNEMENT */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div style={{ flex: 1 }}>
                      <h4 style={{ color: COLORS.primary, fontWeight: '600', margin: 0, lineHeight: '1.3' }}>
                        {tuto.titre_video}
                      </h4>
                    </div>
                    
                    {/* Barre d'actions admin avec icônes et tooltips  */}
{canEdit && (
  <div style={{ marginTop: '30px' }}> {/* AJOUT de marginTop pour descendre */}
    <div className="d-flex align-items-center gap-2">
      {/* Bouton Modifier */}
      <div className="position-relative" data-bs-toggle="tooltip" data-bs-placement="bottom" title="Modifier le tutoriel">
        <button
          className="btn fw-semibold d-flex align-items-center justify-content-center"
          onClick={handleEdit}
          style={{
            color: COLORS.primary,
            borderColor: COLORS.primary,
            backgroundColor: 'transparent',
            borderRadius: '8px',
            width: '40px',
            height: '40px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = COLORS.primary;
            e.target.style.color = COLORS.white;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = COLORS.primary;
          }}
        >
          <i className="bi bi-pencil fs-5"></i>
        </button>
      </div>
      
      {/* Menu déroulant pour changement de statut */}
      <div className="dropdown position-relative" data-bs-toggle="tooltip" data-bs-placement="bottom" title="Changer le statut">
        <button
          className="btn fw-semibold d-flex align-items-center justify-content-center dropdown-toggle"
          type="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          disabled={isChangingStatus}
          style={{
            color: COLORS.info,
            borderColor: COLORS.info,
            backgroundColor: 'transparent',
            borderRadius: '8px',
            width: '40px',
            height: '40px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = COLORS.info;
            e.target.style.color = COLORS.white;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = COLORS.info;
          }}
        >
          {isChangingStatus ? (
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
          ) : (
            <i className="bi bi-arrow-repeat fs-5"></i>
          )}
        </button>
        <ul className="dropdown-menu dropdown-menu-end" style={{
          minWidth: '220px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0',
          padding: '4px'
        }}>
          {(STATUS_OPTIONS[tuto.statut_video] || []).map((option) => (
            <li key={option.value}>
              <button
                className="dropdown-item d-flex align-items-center py-2"
                onClick={() => handleChangeStatus(option.value)}
                disabled={isChangingStatus}
              >
                <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                  width: '28px',
                  height: '28px',
                  backgroundColor: option.color + '20',
                  color: option.color
                }}>
                  <i className={`bi ${option.icon}`}></i>
                </div>
                <div>
                  <div style={{ fontSize: '0.9rem', color: COLORS.darkText }}>{option.label}</div>
                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {option.value === 'publie' ? 'Rendre visible publiquement' :
                     option.value === 'brouillon' ? 'Retirer de la publication' :
                     'Conserver en archives'}
                  </small>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
      
      {/* Bouton Supprimer */}
      <div className="position-relative" data-bs-toggle="tooltip" data-bs-placement="bottom" title="Supprimer le tutoriel">
        <button
          className="btn fw-semibold d-flex align-items-center justify-content-center"
          onClick={handleDeleteTuto}
          style={{
            color: COLORS.danger,
            borderColor: COLORS.danger,
            backgroundColor: 'transparent',
            borderRadius: '8px',
            width: '40px',
            height: '40px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = COLORS.danger;
            e.target.style.color = COLORS.white;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = COLORS.danger;
          }}
        >
          <i className="bi bi-trash fs-5"></i>
        </button>
      </div>
    </div>
  </div>
)}
                  </div>
                  
                  {/* Ligne des badges - EN DESSOUS DES ACTIONS */}
                  <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                    <span className="badge fw-semibold" style={{ 
                      backgroundColor: categoryInfo.color + '20',
                      color: categoryInfo.color,
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '0.85rem'
                    }}>
                      <i className={`bi ${categoryInfo.icon} me-1`}></i>
                      {categoryInfo.label}
                    </span>
                    
                    <span className="badge fw-semibold" style={{ 
                      backgroundColor: statusInfo.bgColor,
                      color: statusInfo.color,
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '0.85rem'
                    }}>
                      <i className={`bi ${statusInfo.icon} me-1`}></i>
                      {statusInfo.text}
                    </span>
                    
                    {tuto.duree_video && (
                      <span className="badge fw-semibold" style={{ 
                        backgroundColor: COLORS.lightBg,
                        color: COLORS.darkText,
                        border: `1px solid #e0e0e0`,
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '0.85rem'
                      }}>
                        <i className="bi bi-clock me-1"></i>
                        {formatDuration(tuto.duree_video)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Messages d'action */}
                {actionMessage && (
                  <div className="alert alert-success alert-dismissible fade show mb-3" style={{ 
                    backgroundColor: '#E8F5E9',
                    borderColor: COLORS.success,
                    color: '#2e7d32',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.success}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-check-circle me-2"></i>
                      <div className="flex-grow-1">{actionMessage}</div>
                      <button type="button" className="btn-close" onClick={() => setActionMessage("")}></button>
                    </div>
                  </div>
                )}

                {actionError && (
                  <div className="alert alert-danger alert-dismissible fade show mb-3" style={{ 
                    backgroundColor: '#FFEBEE',
                    borderColor: COLORS.danger,
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.danger}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">{actionError}</div>
                      <button type="button" className="btn-close" onClick={() => setActionError("")}></button>
                    </div>
                  </div>
                )}

                {/* Lecteur vidéo intégré */}
                {tuto.video_url_complete ? (
                  <div className="mb-4">
                    <VideoPlayer 
                      videoUrl={tuto.video_url_complete}
                      duration={tuto.duree_video}
                      categoryColor={categoryInfo.color}
                    />
                  </div>
                ) : (
                  <div className="mb-4 rounded" style={{ 
                    backgroundColor: '#1a1a1a',
                    aspectRatio: '16/9',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff'
                  }}>
                    <i className="bi bi-film fs-1 mb-3" style={{ color: '#666' }}></i>
                    <span>Vidéo non disponible</span>
                  </div>
                )}

                {/* Description */}
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-card-text"></i>
                    </div>
                    <h6 style={{ color: COLORS.primary, fontWeight: '600', margin: 0 }}>
                      Description
                    </h6>
                  </div>
                  <div style={{ 
                    color: COLORS.darkText,
                    lineHeight: '1.6',
                    whiteSpace: 'pre-line',
                    padding: '16px',
                    borderRadius: '8px',
                    border: `1px solid ${COLORS.lightBg}`
                  }}>
                    {tuto.description_video || 'Aucune description fournie pour ce tutoriel.'}
                  </div>
                </div>

                {/* URL externe si disponible */}
                {tuto.url_video && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center mb-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                        width: '36px',
                        height: '36px',
                        backgroundColor: COLORS.info + '20',
                        color: COLORS.info
                      }}>
                        <i className="bi bi-link-45deg"></i>
                      </div>
                      <h6 style={{ color: COLORS.primary, fontWeight: '600', margin: 0 }}>
                        Lien externe
                      </h6>
                    </div>
                    <div className="card" style={{ border: `1px solid ${COLORS.info}`, borderRadius: '8px' }}>
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between">
                          <div style={{ overflow: 'hidden' }}>
                            <small className="text-muted d-block mb-1">URL de la vidéo</small>
                            <span className="text-truncate d-block" style={{ maxWidth: '300px' }}>
                              {tuto.url_video}
                            </span>
                          </div>
                          <button
                            className="btn fw-semibold"
                            onClick={() => window.open(tuto.url_video, '_blank')}
                            style={{
                              color: COLORS.white,
                              backgroundColor: COLORS.info,
                              borderColor: COLORS.info,
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '0.85rem',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <i className="bi bi-box-arrow-up-right me-1"></i>
                            Ouvrir
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar avec informations */}
          <div className="col-lg-4">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none',
              position: 'sticky',
              top: '20px'
            }}>
              <div className="card-body">
                <h6 style={{ 
                  color: COLORS.primary, 
                  fontWeight: '600',
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '12px',
                  marginBottom: '16px'
                }}>
                  <i className="bi bi-info-circle me-2"></i>
                  Informations détaillées
                </h6>

                <div className="mb-4">
                  {/* Date de publication */}
                  <div className="d-flex align-items-center mb-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-calendar"></i>
                    </div>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>Publié le</small>
                      <span style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem' }}>
                        {formatDateTime(tuto.date_publication || tuto.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* Durée */}
                  <div className="d-flex align-items-center mb-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-clock-history"></i>
                    </div>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>Durée</small>
                      <span style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem' }}>
                        {tuto.duree_video ? formatDuration(tuto.duree_video) : 'Non spécifiée'}
                      </span>
                    </div>
                  </div>

                  {/* Catégorie */}
                  <div className="d-flex align-items-center mb-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className={`bi ${categoryInfo.icon}`}></i>
                    </div>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>Catégorie</small>
                      <span style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem' }}>
                        {categoryInfo.label}
                      </span>
                    </div>
                  </div>

                  {/* Statut */}
                  <div className="d-flex align-items-center mb-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: statusInfo.bgColor,
                      color: statusInfo.color
                    }}>
                      <i className={`bi ${statusInfo.icon}`}></i>
                    </div>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>Statut</small>
                      <span style={{ color: statusInfo.color, fontWeight: '500', fontSize: '0.9rem' }}>
                        {statusInfo.text}
                      </span>
                    </div>
                  </div>

                  {/* Date de création */}
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-calendar-plus"></i>
                    </div>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>Créé le</small>
                      <span style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem' }}>
                        {formatDateTime(tuto.created_at)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Bouton retour avec message admin intégré */}
                <div className="mt-4 pt-3 border-top" style={{ borderColor: COLORS.lightBg }}>
                  <div className="d-grid gap-3">
                    <button
                      className="btn fw-semibold w-100"
                      onClick={handleGoBack}
                      style={{
                        color: COLORS.primary,
                        borderColor: COLORS.primary,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '12px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = COLORS.primary;
                        e.target.style.color = COLORS.white;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = COLORS.primary;
                      }}
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      Retour aux tutoriels
                    </button>
                    
                    {/* Message pour les administrateurs - Maintenant sous le bouton */}
                    {canEdit && (
                      <div className="alert alert-info border-0 p-3" style={{ 
                        backgroundColor: '#E3F2FD',
                        color: COLORS.primary,
                        borderRadius: '8px',
                        marginBottom: 0
                      }}>
                        <div className="d-flex align-items-start">
                          <div className="me-3">
                            <i className="bi bi-shield-check fs-5"></i>
                          </div>
                          <div>
                            <small className="fw-semibold d-block mb-1">Mode administrateur</small>
                            <p className="mb-0 small" style={{ color: COLORS.darkText }}>
                              Vous pouvez modifier ce tutoriel ou le gérer depuis la liste admin.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && tuto && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '12px', border: 'none' }}>
              <div className="modal-header border-0 pb-0">
                <div className="modal-title w-100">
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                      width: '40px',
                      height: '40px',
                      backgroundColor: '#FFEBEE',
                      color: COLORS.danger
                    }}>
                      <i className="bi bi-exclamation-triangle"></i>
                    </div>
                    <div>
                      <h5 style={{ color: COLORS.danger, fontWeight: '600', margin: 0 }}>
                        Confirmer la suppression
                      </h5>
                      <small className="text-muted">Action irréversible</small>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteModal(false)}
                ></button>
              </div>
              
              <div className="modal-body py-4">
                <div className="alert alert-danger mb-4" style={{ 
                  backgroundColor: '#FFEBEE',
                  borderColor: COLORS.danger,
                  color: '#c62828',
                  borderRadius: '8px'
                }}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle-fill me-2 fs-5"></i>
                    <div>
                      <strong>Attention !</strong> Cette action est définitive.
                    </div>
                  </div>
                </div>
                
                <p style={{ color: COLORS.darkText }}>
                  Vous êtes sur le point de supprimer définitivement :
                </p>
                
                <div className="card mb-3" style={{ 
                  backgroundColor: '#FFF3E0',
                  border: `1px solid ${COLORS.warning}`,
                  borderRadius: '8px'
                }}>
                  <div className="card-body">
                    <div className="d-flex align-items-start">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                        width: '32px',
                        height: '32px',
                        backgroundColor: '#FF9800',
                        color: '#fff'
                      }}>
                        <i className="bi bi-film"></i>
                      </div>
                      <div style={{ flex: 1 }}>
                        <h6 style={{ color: COLORS.primary, marginBottom: '4px' }}>
                          {tuto.titre_video}
                        </h6>
                        <div className="d-flex flex-wrap gap-1">
                          <span className="badge" style={{ 
                            backgroundColor: getCategoryInfo(tuto.categorie_video).color + '20',
                            color: getCategoryInfo(tuto.categorie_video).color,
                            fontSize: '0.75rem'
                          }}>
                            {getCategoryInfo(tuto.categorie_video).label}
                          </span>
                          <span className="badge" style={{ 
                            backgroundColor: getStatusInfo(tuto.statut_video, tuto.actif_video).bgColor,
                            color: getStatusInfo(tuto.statut_video, tuto.actif_video).color,
                            fontSize: '0.75rem'
                          }}>
                            {getStatusInfo(tuto.statut_video, tuto.actif_video).text}
                          </span>
                          <span className="badge" style={{ 
                            backgroundColor: '#E0E0E0',
                            color: COLORS.darkText,
                            fontSize: '0.75rem'
                          }}>
                            ID: {tuto.id_video}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="alert alert-warning mb-0" style={{ fontSize: '0.9rem' }}>
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>Conséquences :</strong> Le tutoriel ne sera plus accessible, 
                  même pour les administrateurs. Toutes les données associées seront supprimées définitivement.
                </div>
              </div>
              
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn fw-semibold"
                  onClick={() => setShowDeleteModal(false)}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  <i className="bi bi-x-lg me-2"></i>
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn fw-semibold"
                  onClick={confirmDeleteTuto}
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.danger,
                    borderColor: COLORS.danger,
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  <i className="bi bi-trash me-2"></i>
                  Supprimer définitivement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .alert strong {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .form-select,
        .form-text,
        .alert,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(11, 60, 93, 0.25);
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .text-truncate {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .dropdown-item:active {
          background-color: #E3F2FD;
          color: ${COLORS.primary};
        }
        
        .dropdown-item:hover {
          background-color: #F5F7FA;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .col-lg-8, .col-lg-4 {
            padding: 0;
          }
          
          .video-player-container {
            border-radius: 0 !important;
          }
          
          .d-flex.align-items-center.gap-2 {
            justify-content: center;
            margin-top: 16px;
          }
        }
        
        @media (max-width: 576px) {
          .badge {
            font-size: 0.75rem !important;
            padding: 4px 8px !important;
          }
          
          h4 {
            font-size: 1.25rem;
          }
          
          .btn[data-bs-toggle="dropdown"] {
            width: 40px !important;
            height: 40px !important;
          }
          
          .modal-dialog {
            margin: 16px;
          }
        }
        
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </Layout>
  );
};

export default TutorialDetails;